import React, { ComponentPropsWithoutRef, ElementType } from 'react'
import { ScrollMenu as BaseScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu-alt'

import { ChevronLeft, ChevronRight } from '@makyoapp/makyo-icon/icon'

import './ScrollMenu.css'

import clsx from 'clsx'

function useVisibilityContext() {
  return React.useContext(VisibilityContext)
}

function Arrow({
  children,
  disabled,
  onClick,
  className,
}: {
  children: React.ReactNode
  disabled: boolean
  onClick: VoidFunction
  className?: string
}) {
  return (
    <button
      disabled={disabled}
      className={clsx(
        'scroll-menu-arrow absolute z-[20] w-5 h-full flex items-center justify-center bg-zinc-200 dark:bg-slate-900',
        className
      )}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function LeftArrow() {
  const { isFirstItemVisible, scrollPrev, visibleElements, initComplete } = useVisibilityContext()

  const [disabled, setDisabled] = React.useState(!initComplete || (initComplete && isFirstItemVisible))
  React.useEffect(() => {
    // NOTE: detect if whole component visible
    if (visibleElements.length) {
      setDisabled(isFirstItemVisible)
    }
  }, [isFirstItemVisible, visibleElements])

  return (
    <Arrow disabled={disabled} className='arrow-left' onClick={() => scrollPrev()}>
      <ChevronLeft className='shrink-0' />
    </Arrow>
  )
}

function RightArrow() {
  const { isLastItemVisible, scrollNext, visibleElements } = useVisibilityContext()

  const [disabled, setDisabled] = React.useState(!visibleElements.length && isLastItemVisible)
  React.useEffect(() => {
    if (visibleElements.length) {
      setDisabled(isLastItemVisible)
    }
  }, [isLastItemVisible, visibleElements])

  return (
    <Arrow disabled={disabled} className='arrow-right' onClick={() => scrollNext()}>
      <ChevronRight className='shrink-0' />
    </Arrow>
  )
}

export type ItemType = React.ReactElement<{
  /**
   Required. id for every item, should be unique
   */
  itemId: string
}>

type ScrollMenuItemProps = ComponentPropsWithoutRef<ElementType> & {
  itemId: string
  children?: React.ReactNode
  tag?: keyof JSX.IntrinsicElements
}

export function ScrollMenuItem({ itemId, children, tag: Comp = 'div', ...rest }: ScrollMenuItemProps) {
  const { isItemVisible } = useVisibilityContext()
  const visible = isItemVisible(itemId)

  return (
    <Comp data-scroll-visible={visible} {...rest}>
      {children}
    </Comp>
  )
}

export function scrollMenuOnWheel(apiObj: React.ContextType<typeof VisibilityContext>, ev: React.WheelEvent): void {
  const isTouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15
  if (isTouchpad) {
    ev.stopPropagation()
    return
  }

  if (ev.deltaY < 0) {
    apiObj?.scrollNext()
  } else if (ev.deltaY > 0) {
    apiObj?.scrollPrev()
  }
}

export function ScrollMenu({
  children,
  className,
  wrapperClassName,
}: {
  children: null | ItemType | ItemType[]
  className?: string
  wrapperClassName?: string
}) {
  return (
    <BaseScrollMenu
      LeftArrow={LeftArrow}
      RightArrow={RightArrow}
      wrapperClassName={wrapperClassName}
      scrollContainerClassName={clsx(className, 'w-full')}
    >
      {children as ItemType}
    </BaseScrollMenu>
  )
}
